import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../../../../shared/lib/supabase'
import { useTechnologyStack } from '../../../../assessment/hooks/useTechnologyStack'
import { usePolicyInventory } from '../../../../policies/hooks/usePolicyInventory'
import { relativeTime } from '../../../../../shared/utils/clientHelpers'
import { calculateScores } from '../../../../../shared/utils/scoring'
import IntakeStatusCard from '../IntakeStatusCard'
import AssessmentsCard from '../AssessmentsCard'

function OverviewTab({ client }) {
  const [attentionItems, setAttentionItems] = useState([])
  const [clientPolicies, setClientPolicies] = useState([])
  const [showActivity, setShowActivity] = useState(false)
  const [criticalSystemsCount, setCriticalSystemsCount] = useState(0)
  const [tier1Count, setTier1Count] = useState(0)
  const { technologies } = useTechnologyStack(client.id)
  const { stats: policyStats } = usePolicyInventory(client)

  const activeAssessments = useMemo(() =>
    (client.assessments || []).filter(a => a.status === 'in_progress'),
    [client.assessments]
  )
  const completedAssessments = useMemo(() =>
    (client.assessments || [])
      .filter(a => a.status === 'completed')
      .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt)),
    [client.assessments]
  )


  // Fetch policies for attention items and engagement pulse
  useEffect(() => {
    if (!client.id) return
    const fetchPolicies = async () => {
      const { data } = await supabase
        .from('client_policies')
        .select('*')
        .eq('client_id', client.id)
        .order('domain')
      setClientPolicies(data || [])
    }
    fetchPolicies()
  }, [client.id])

  // Fetch critical systems count
  useEffect(() => {
    if (!client.id) return
    const fetchCriticalSystems = async () => {
      const { data } = await supabase
        .from('client_critical_systems')
        .select('criticality_tier')
        .eq('client_id', client.id)
      setCriticalSystemsCount(data?.length || 0)
      setTier1Count(data?.filter(s => s.criticality_tier === 'critical').length || 0)
    }
    fetchCriticalSystems()
  }, [client.id])

  // Generate attention items from real data
  useEffect(() => {
    const items = []
    const now = new Date()

    // Stalled assessments (in_progress, not updated in 5+ days)
    activeAssessments.forEach(a => {
      const updated = new Date(a.updatedAt || a.createdAt)
      const daysSince = Math.floor((now - updated) / 86400000)
      if (daysSince >= 5) {
        items.push({
          id: `stalled-${a.id}`,
          severity: 'amber',
          title: 'Stalled assessment',
          subtitle: a.title || 'Untitled assessment',
          age: `${daysSince}d inactive`
        })
      }
    })

    // Aging draft policies (draft, created 14+ days ago)
    clientPolicies.filter(p => p.status === 'draft').forEach(p => {
      const created = new Date(p.created_at)
      const daysSince = Math.floor((now - created) / 86400000)
      if (daysSince >= 14) {
        items.push({
          id: `draft-${p.id}`,
          severity: 'blue',
          title: 'Aging draft policy',
          subtitle: p.title,
          age: `${daysSince}d in draft`
        })
      }
    })

    // Policies nearing review (updated 11+ months ago)
    clientPolicies.filter(p => p.status === 'published').forEach(p => {
      const updated = new Date(p.updated_at)
      const daysSince = Math.floor((now - updated) / 86400000)
      if (daysSince >= 330) {
        items.push({
          id: `review-${p.id}`,
          severity: 'amber',
          title: 'Policy nearing annual review',
          subtitle: p.title,
          age: `${Math.floor(daysSince / 30)}mo since last update`
        })
      }
    })

    setAttentionItems(items)
  }, [activeAssessments, clientPolicies])

  // Engagement pulse metrics — compute from responses for accuracy after edits
  const computedScores = useMemo(() => {
    const latest = completedAssessments[0]
    if (!latest?.responses?.maturityAssessment) return null
    try { return calculateScores(latest.responses) } catch { return null }
  }, [completedAssessments[0]?.responses])
  const latestScore = computedScores?.overall?.score ?? completedAssessments[0]?.scores?.overall?.score
  const latestLevel = computedScores?.overall?.maturityLevel?.label ?? completedAssessments[0]?.scores?.overall?.maturityLevel?.label
  const previousScore = completedAssessments[1]?.scores?.overall?.score
  const scoreTrend = latestScore !== undefined && previousScore !== undefined
    ? latestScore - previousScore : null

  // Build recent activity from real data
  const activityItems = []
  completedAssessments.slice(0, 2).forEach(a => {
    activityItems.push({
      id: `completed-${a.id}`,
      color: 'bg-green-500',
      action: 'Assessment completed',
      detail: a.title || 'Discovery assessment',
      time: relativeTime(a.completedAt)
    })
  })
  clientPolicies.filter(p => p.status === 'published').slice(0, 2).forEach(p => {
    activityItems.push({
      id: `published-${p.id}`,
      color: 'bg-blue-500',
      action: 'Policy published',
      detail: p.title,
      time: relativeTime(p.updated_at)
    })
  })
  activeAssessments.slice(0, 1).forEach(a => {
    activityItems.push({
      id: `started-${a.id}`,
      color: 'bg-amber-500',
      action: 'Assessment started',
      detail: a.title || 'Discovery assessment',
      time: relativeTime(a.createdAt)
    })
  })

  return (
    <div className="space-y-6">
      {/* Needs Attention */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden border-l-[3px] border-l-yellow-500">
        <div className="bg-yellow-50 dark:bg-yellow-900/10 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-4.5 h-4.5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">Needs Attention</span>
          </div>
          {attentionItems.length > 0 && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-300">
              {attentionItems.length}
            </span>
          )}
        </div>

        {attentionItems.length === 0 ? (
          <div className="px-4 py-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-green-700 dark:text-green-400 font-medium">All clear — no items need attention</span>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {attentionItems.map(item => (
              <div key={item.id} className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  item.severity === 'red' ? 'bg-red-500'
                  : item.severity === 'amber' ? 'bg-amber-500'
                  : 'bg-blue-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{item.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.subtitle}</p>
                </div>
                <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">{item.age}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Client Intake Status */}
      <IntakeStatusCard client={client} />

      {/* Engagement Pulse */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Maturity Score */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4" style={{ borderTopWidth: '2px', borderTopColor: '#8AD0D6' }}>
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Maturity Score</p>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-gray-900 dark:text-white">
              {latestScore !== undefined ? `${latestScore}%` : '—'}
            </span>
            {scoreTrend !== null && (
              <span className={`text-xs font-semibold ${scoreTrend >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                {scoreTrend >= 0 ? '+' : ''}{scoreTrend}%
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {completedAssessments.length > 0
              ? latestLevel || 'Scored'
              : 'No assessments'}
          </p>
        </div>

        {/* Policy Coverage */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4" style={{ borderTopWidth: '2px', borderTopColor: '#22C55E' }}>
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Policy Coverage</p>
          <span className="text-2xl font-extrabold text-gray-900 dark:text-white">
            {policyStats.totalRecommended > 0 ? `${policyStats.adopted} / ${policyStats.totalRecommended}` : '—'}
          </span>
          {policyStats.totalRecommended > 0 && (
            <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-600 mt-2">
              <div
                className="h-1.5 rounded-full"
                style={{
                  width: `${Math.round((policyStats.adopted / policyStats.totalRecommended) * 100)}%`,
                  background: 'linear-gradient(90deg, #22C55E, #8AD0D6)'
                }}
              />
            </div>
          )}
          <div className="mt-1.5 flex flex-wrap gap-1">
            {policyStats.totalRecommended > 0
              ? (client.regulatory_frameworks || []).slice(0, 3).map(f => (
                  <span key={f} className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300">
                    {f}
                  </span>
                ))
              : <p className="text-xs text-gray-400 dark:text-gray-500">Complete assessment to generate</p>
            }
          </div>
        </div>

        {/* Assets */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4" style={{ borderTopWidth: '2px', borderTopColor: '#F59E0B' }}>
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Assets</p>
          <span className="text-2xl font-extrabold text-gray-900 dark:text-white">{criticalSystemsCount}</span>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {criticalSystemsCount === 0 ? 'not documented' : `${tier1Count} Tier 1 critical`}
          </p>
        </div>

        {/* Security Tools */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4" style={{ borderTopWidth: '2px', borderTopColor: '#6366F1' }}>
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Security Tools</p>
          <span className="text-2xl font-extrabold text-gray-900 dark:text-white">{technologies.length}</span>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">in tech stack</p>
        </div>
      </div>

      {/* Assessments */}
      <AssessmentsCard client={client} />

      {/* Recent Activity (collapsible) */}
      {activityItems.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <button
            onClick={() => setShowActivity(!showActivity)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">Recent Activity</span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                {activityItems.length}
              </span>
            </div>
            <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showActivity ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showActivity && (
            <div className="border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
              {activityItems.map(item => (
                <div key={item.id} className="px-4 py-3 flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.color}`} />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-900 dark:text-white">{item.action}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400"> — {item.detail}</span>
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">{item.time}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default OverviewTab
